/**
 * The wire shapes `Rws.AiInsights.Host.Reporting.OrganisationReportingEndpoints` returns
 * (phase 14, `AIA-81`): the unused-entitlement screen (dormancy and departed-identity candidates,
 * plus the people who cannot be assessed at all), the unattributed-consumption report, the
 * organisation-spend view, the `SC-9` baseline, and the administrator decision on a finding.
 * Follows `api/reporting.ts`'s own pattern exactly: plain `fetch`, `credentials: "same-origin"`,
 * and a discriminated-union result instead of throwing.
 */
import { apiFetch } from "./apiFetch";

/** One vendor's completeness for a leadership-facing screen — never a silent omission. */
export interface VendorCompletenessResponse {
  vendorId: string;
  vendorDisplayName: string;
  hasEverSynced: boolean;
  lastSuccessfulSyncAt: string | null;
  note: string | null;
}

/** The body of `GET /api/reporting/me/allowance`; contains only the signed-in person's allowance data. */
export interface MyAllowanceResponse {
  personId: string;
  periodStart: string;
  periodEnd: string;
  effectiveAllowanceUsd: number;
  contributingGroupPaths: string[][];
  spendUsd: number;
  remainingUsd: number;
  isOverAllowance: boolean;
  isInformationalOnly: boolean;
}

/** One dormancy or departed-identity candidate, always with its evidence and caveat. */
export interface UnusedEntitlementCandidateResponse {
  vendorId: string;
  vendorDisplayName: string;
  personId: string;
  /** Empty when no name is recorded. */
  personName: string;
  /** Empty when the person is not known. */
  personEmail: string;
  findingKind: "dormancy" | "departed";
  evidence: string;
  /** At least this many days without use, or holding the seat when it was never used; null for a departed finding. */
  daysSinceLastUse: number | null;
  /** The first day of the last month of use (`2026-08-01`); null when never used. */
  lastUsedMonth: string | null;
  /** When the portal first saw the seat held; null when not known. */
  seatSince: string | null;
  disappearedFromMembersAt: string | null;
  caveat: string;
  existingDecision: "confirmed" | "dismissed" | null;
  existingDecisionAt: string | null;
  existingDecisionNote: string | null;
  /** Whether the portal allocates the person this tool: confirming then asks to switch it off; otherwise, to remove the account. */
  allocated: boolean;
}

/** One person who could not be assessed for a vendor — unknown, never unused. */
export interface UnusedEntitlementUnknownResponse {
  vendorId: string;
  vendorDisplayName: string;
  personId: string;
  personName: string;
  personEmail: string;
  reason: string;
}

/** The body of `GET /api/reporting/unused-entitlement`. */
export interface UnusedEntitlementResponse {
  generatedAt: string;
  thresholdDays: number;
  vendorCompleteness: VendorCompletenessResponse[];
  candidates: UnusedEntitlementCandidateResponse[];
  unknown: UnusedEntitlementUnknownResponse[];
}

/** One month of an unattributed account's figures. Mirrors `UnattributedConsumptionMonthResponse`. */
export interface UnattributedConsumptionMonthResponse {
  periodStart: string;
  /** US dollars; null when it could not be converted. */
  costUsd: number | null;
  requests: number;
}

/** One provider account whose spend no person carries (`TD-19`). Mirrors `UnattributedConsumptionEntryResponse`. */
export interface UnattributedConsumptionEntryResponse {
  vendorSubjectId: string;
  vendorId: string;
  vendorDisplayName: string;
  vendorNativeId: string;
  /** US dollars, over every month; null when a cost could not be converted. */
  totalCostUsd: number | null;
  firstMonth: string;
  /** The first day of the last month of use; null when never used. */
  lastUsedMonth: string | null;
  /** When the vendor last reported the account, which is not when it was used. */
  latestObservedAt: string;
  hasPendingIdentityQueueEntry: boolean;
  identityQueueCandidateId: string | null;
  /** Oldest first. */
  months: UnattributedConsumptionMonthResponse[];
}

/** The body of `GET /api/reporting/unattributed`. */
export interface UnattributedConsumptionResponse {
  generatedAt: string;
  vendorCompleteness: VendorCompletenessResponse[];
  entries: UnattributedConsumptionEntryResponse[];
}

/** One organisational unit's (division, group or derived cost centre) total spend. */
export interface OrganisationUnitSpendResponse {
  unitLabel: string;
  totalSpend: number | null;
  currency: string;
  wasFullyConverted: boolean;
  personCount: number;
  /** The division's, department's or group's id, for its page; null for spend in no unit ("Unclassified"). */
  unitId?: string | null;
}

/** One vendor's aggregated usage for a "My Groups" group, mirroring the per-tool cards "My Usage" shows for a single person. */
export interface GroupVendorUsageResponse {
  vendorId: string;
  vendorDisplayName: string;
  totalSpend: number | null;
  currency: string;
  wasFullyConverted: boolean;
  personCount: number;
}

/**
 * One division, department or entitlement group the current person is a member of, owns, or sits beneath something they own, and its
 * aggregated spend, from `GET /api/reporting/me/groups`.
 */
export interface MyGroupUsageResponse {
  groupId: string;
  groupLabel: string;
  /** Whether this is a `Division`, a `Department` or a `Group`. */
  kind: "Division" | "Department" | "Group";
  /** Whether the current person is a direct member of the group. Divisions and departments never are. */
  isMember: boolean;
  /** Whether the current person is its owner. */
  isOwner: boolean;
  /** Whether it is shown only because the person owns something it sits beneath. */
  isWithinOwned: boolean;
  /** The names above it, outermost first: its division, its department, then the groups it sits beneath. Empty for a division. */
  breadcrumbs: string[];
  totalSpend: number | null;
  currency: string;
  wasFullyConverted: boolean;
  personCount: number;
  /** How many people hold an active membership of this group itself, not counting groups beneath it. Zero for a division or department. */
  directMemberCount: number;
  /** The monthly allowance of the group and everyone beneath it, or `null` when there is none to total (a division or department). */
  totalAllowance: number | null;
  vendors: GroupVendorUsageResponse[];
  /** How many of the people whose spend it reports are near a spending limit (90% or more of one), this month only. */
  peopleNearLimit: number;
  /** How many of them have spent a whole limit or more, this month only. */
  peopleAtLimit: number;
  /** The units and groups in `breadcrumbs`, with whether the viewer can open each one's page; null when not known. */
  ancestors?: GroupAncestorResponse[] | null;
  /** Whether the viewer sees it only because they may look at the whole organisation, from the Organisation page. */
  isOrganisationView?: boolean;
}

/** One division, department or group above a group, for its breadcrumb. */
export interface GroupAncestorResponse {
  groupId: string;
  name: string;
  /** Whether the viewer can open its own page: it is one of the groups My Groups shows them. */
  canOpen: boolean;
}

/** The body of `GET /api/reporting/me/groups`. */
export interface MyGroupsResponse {
  generatedAt: string;
  periodStart: string;
  periodEnd: string;
  currency: string;
  vendorCompleteness: VendorCompletenessResponse[];
  groups: MyGroupUsageResponse[];
}

/** One vendor's total spend across a group on a single day. */
export interface GroupDailySpendVendorAmountResponse {
  vendorId: string;
  amount: number;
}

/** One day's total spend across everyone in one of the current person's own groupings. */
export interface GroupDailySpendDayResponse {
  date: string;
  totalAmount: number;
  currency: string;
  vendors: GroupDailySpendVendorAmountResponse[];
}

/** One of the current person's own groupings and its day-by-day spend series for the current period. Also reused for the whole-organisation series, keyed as "organisation". */
export interface GroupDailySpendSeriesResponse {
  groupKind: string;
  days: GroupDailySpendDayResponse[];
  /** The period-end projection of this series' running total, or `null`/absent when not computed. */
  projection?: SpendProjectionResponse | null;
  /** The seat fees charged this period, which `days` leaves out (the days are usage); the running total starts at them. */
  seatFees?: number;
}

/** The body of `GET /api/reporting/me/groups/daily-spend`. */
export interface MyGroupsDailySpendResponse {
  from: string;
  to: string;
  groups: GroupDailySpendSeriesResponse[];
}

/** One organisational unit's total spend on a single day, for an Organisation chart panel's time-series view. */
export interface OrganisationDailySpendUnitAmountResponse {
  unit: string;
  amount: number;
}

/** One day's total spend across the whole organisation, broken down by unit within one grouping. */
export interface OrganisationDailySpendDayResponse {
  date: string;
  totalAmount: number;
  currency: string;
  units: OrganisationDailySpendUnitAmountResponse[];
}

/** One Organisation grouping and its day-by-day spend series for the current period. */
export interface OrganisationDailySpendSeriesResponse {
  groupKind: "division" | "department" | "group" | "model";
  days: OrganisationDailySpendDayResponse[];
}

/** The body of `GET /api/reporting/organisation/spend/daily-spend`. */
export interface OrganisationDailySpendResponse {
  byDivision: OrganisationDailySpendSeriesResponse;
  byDepartment: OrganisationDailySpendSeriesResponse;
  byGroup: OrganisationDailySpendSeriesResponse;
  /** The whole organisation's day-by-day spend, broken down by AI model (each model is a unit). Only vendors that report spend per model contribute. */
  byModel: OrganisationDailySpendSeriesResponse;
  /** The whole organisation's day-by-day spend, broken down by vendor rather than by unit - powers the top-of-page total spend chart. */
  byOrganisation: GroupDailySpendSeriesResponse;
  /** The month-end projection of the organisation-wide running total (weekday/weekend rates over the last 7 complete days). */
  projection?: SpendProjectionResponse;
}

/** One day on a projected running-total line. */
export interface SpendProjectionPointResponse {
  date: string;
  cumulativeAmount: number;
}

/** A month-end spend projection. */
export interface SpendProjectionResponse {
  projectedTotal: number;
  points: SpendProjectionPointResponse[];
}

/** One vendor's cost-and-uptake comparison row (`UR-L-05`). */
export interface ToolComparisonResponse {
  vendorId: string;
  vendorDisplayName: string;
  totalSpend: number | null;
  currency: string;
  entitledPersonCount: number;
  activePersonCount: number;
  uptakePercent: number | null;
  seatCostIsEstimate: boolean;
}

/** The body of `GET /api/reporting/organisation/spend`. */
export interface OrganisationSpendResponse {
  generatedAt: string;
  periodStart: string;
  periodEnd: string;
  vendorCompleteness: VendorCompletenessResponse[];
  totalSpend: number | null;
  totalSpendWasFullyConverted: boolean;
  currency: string;
  totalAllowance: number | null;
  totalAllowanceWasFullyConverted: boolean;
  byDivision: OrganisationUnitSpendResponse[];
  byDepartment: OrganisationUnitSpendResponse[];
  byGroup: OrganisationUnitSpendResponse[];
  toolComparison: ToolComparisonResponse[];
}

/** A vendor on the organisation trend, with the first month of the range its spend and its use were recorded in. */
export interface OrganisationTrendVendorResponse {
  vendorId: string;
  displayName: string;
  /** `YYYY-MM-DD`, or null when no spend is recorded in the range. A month before it says nothing about this vendor's spend. */
  firstMonthWithSpend: string | null;
  /** `YYYY-MM-DD`, or null when no use is recorded in the range. */
  firstMonthWithActivity: string | null;
}

/** One vendor's figures for one month of the organisation trend. */
export interface OrganisationTrendMonthVendorResponse {
  vendorId: string;
  spend: number | null;
  /** People who used the vendor in the month (a request or an interaction), not merely held a seat. */
  activePeople: number;
  /** People who carried a cost with the vendor in the month, used or not. */
  payingPeople: number;
  /** What the people who paid and did not use the vendor cost in the month: seat fees for nothing. Zero for a vendor billed by use. */
  idleSpend: number;
  /** People who had the vendor in the month: a member of its account, or anyone who paid for or used it. */
  allocatedPeople: number;
}

/** One month of the organisation trend. */
export interface OrganisationTrendMonthResponse {
  periodStart: string;
  isCurrentMonth: boolean;
  totalSpend: number | null;
  wasFullyConverted: boolean;
  activePeople: number;
  /** Of the month's active people, how many were also active the month before; null for the first month of the range. */
  returningPeople: number | null;
  /** The share of the month's spend, as a percentage, from the tenth of people who spent most; null when nothing was spent. */
  topTenthSharePercent: number | null;
  vendors: OrganisationTrendMonthVendorResponse[];
}

/** The body of `GET /api/reporting/organisation/trend`: the organisation's spend and use of AI tools, month by month, oldest first. */
export interface OrganisationTrendResponse {
  generatedAt: string;
  currency: string;
  vendors: OrganisationTrendVendorResponse[];
  months: OrganisationTrendMonthResponse[];
}

/** A vendor named by the per-person usage response, in the order the portal lists vendors. */
export interface OrganisationPersonUsageVendor {
  vendorId: string;
  vendorDisplayName: string;
}

/** One person's spend with one vendor. */
export interface OrganisationPersonVendorSpend {
  vendorId: string;
  totalSpend: number | null;
  wasFullyConverted: boolean;
}

/** One person's spend for the period, summed across every vendor and split by vendor. Names an individual, so it is only read by people with `consumption.read.others`. */
export interface OrganisationPersonUsageRow {
  personId: string;
  displayName: string;
  totalSpend: number | null;
  wasFullyConverted: boolean;
  byVendor: OrganisationPersonVendorSpend[];
  /** How close the person is to a spending limit this month, on a group's people list for the current month; otherwise absent or null. */
  limit?: PersonLimitResponse | null;
}

/** How close a person is to their spending limit this month, on the provider where they are closest. */
export interface PersonLimitResponse {
  vendorId: string;
  limitUsd: number;
  spend: number;
  /** The spend as a percentage of the limit, unrounded; may pass 100. */
  percentUsed: number;
  /** `within`, `near` (90% of the limit or more) or `reached` (the whole limit or more). */
  status: "within" | "near" | "reached";
}

/** The body of `GET /api/reporting/organisation/usage-by-person`: every person with spend in the period, largest first. */
export interface OrganisationPersonUsageResponse {
  generatedAt: string;
  periodStart: string;
  periodEnd: string;
  currency: string;
  vendors: OrganisationPersonUsageVendor[];
  people: OrganisationPersonUsageRow[];
}

/** One vendor's row of the `SC-9` unused-seat baseline. */
export interface UnusedSeatBaselineSnapshotResponse {
  capturedAt: string;
  vendorId: string;
  vendorDisplayName: string;
  totalEntitledCount: number;
  unusedCount: number;
  unknownCount: number;
  thresholdDaysUsed: number;
}

/** The outcome of capturing a new `SC-9` baseline snapshot. */
export interface CaptureBaselineResponse {
  capturedAt: string;
  isFirstCapture: boolean;
  vendorCount: number;
}

/**
 * The outcome of a read against one of this phase's endpoints. `denied` is a `403` from the
 * permission-enforcement middleware, modelled distinctly from `error` so the UI can show
 * `PermissionDeniedState` rather than a generic failure.
 */
export type OrganisationQueryResult<TResponse> =
  | { kind: "ok"; response: TResponse }
  | { kind: "denied" }
  | { kind: "error"; message: string }
  | { kind: "aborted" };

const unusedEntitlementPath = "/api/reporting/unused-entitlement";
const unusedEntitlementExportPath = "/api/reporting/unused-entitlement/export";
const decisionsPath = "/api/reporting/unused-entitlement/decisions";
const baselinePath = "/api/reporting/unused-entitlement/baseline";
const baselineExportPath = "/api/reporting/unused-entitlement/baseline/export";
const baselineCapturePath = "/api/reporting/unused-entitlement/baseline/capture";
const unattributedPath = "/api/reporting/unattributed";
const unattributedExportPath = "/api/reporting/unattributed/export";
const organisationSpendPath = "/api/reporting/organisation/spend";
const organisationSpendExportPath = "/api/reporting/organisation/spend/export";
const myGroupsPath = "/api/reporting/me/groups";
const myAllowancePath = "/api/reporting/me/allowance";
const myGroupsDailySpendPath = "/api/reporting/me/groups/daily-spend";
const organisationDailySpendPath = "/api/reporting/organisation/spend/daily-spend";
const organisationPersonUsagePath = "/api/reporting/organisation/usage-by-person";
const organisationTrendPath = "/api/reporting/organisation/trend";

/**
 * Calls `GET /api/reporting/unused-entitlement` and interprets the result.
 *
 * @returns Every dormancy/departed candidate and every unknown person, a permission denial, or an error.
 */
export async function fetchUnusedEntitlement(): Promise<OrganisationQueryResult<UnusedEntitlementResponse>> {
  return fetchOrganisationJson<UnusedEntitlementResponse>(unusedEntitlementPath, "unused-entitlement");
}

/** Fetches only the signed-in person's effective allowance and current UTC-month spend. */
export async function fetchMyAllowance(signal?: AbortSignal): Promise<OrganisationQueryResult<MyAllowanceResponse>> {
  return fetchOrganisationJson<MyAllowanceResponse>(myAllowancePath, "my-allowance", signal);
}

/**
 * Calls `GET /api/reporting/unattributed` and interprets the result.
 *
 * @returns Every unattributed vendor identity, a permission denial, or an error.
 */
export async function fetchUnattributedConsumption(): Promise<OrganisationQueryResult<UnattributedConsumptionResponse>> {
  return fetchOrganisationJson<UnattributedConsumptionResponse>(unattributedPath, "unattributed");
}

/**
 * Calls `GET /api/reporting/organisation/spend` and interprets the result.
 *
 * @param periodStart - The first day of the reporting period (`YYYY-MM-DD`), or omitted for the current period.
 * @returns The organisation-spend view, a permission denial, or an error.
 */
export async function fetchOrganisationSpend(
  periodStart?: string,
  signal?: AbortSignal,
): Promise<OrganisationQueryResult<OrganisationSpendResponse>> {
  const path = periodStart ? `${organisationSpendPath}?periodStart=${encodeURIComponent(periodStart)}` : organisationSpendPath;
  return fetchOrganisationJson<OrganisationSpendResponse>(path, "organisation-spend", signal);
}

/**
 * Calls `GET /api/reporting/unused-entitlement/baseline` and interprets the result.
 *
 * @returns Every captured `SC-9` baseline row, a permission denial, or an error.
 */
export async function fetchUnusedSeatBaseline(): Promise<OrganisationQueryResult<UnusedSeatBaselineSnapshotResponse[]>> {
  return fetchOrganisationJson<UnusedSeatBaselineSnapshotResponse[]>(baselinePath, "unused-seat-baseline");
}

/**
 * Calls `GET /api/reporting/me/groups` and interprets the result.
 *
 * @param periodStart - The first day of the reporting period (`YYYY-MM-DD`), or omitted for the current period.
 * @returns The current person's own division, group and cost centre with their aggregated usage, a permission denial, or an error.
 */
export async function fetchMyGroups(periodStart?: string, signal?: AbortSignal): Promise<OrganisationQueryResult<MyGroupsResponse>> {
  const path = periodStart ? `${myGroupsPath}?periodStart=${encodeURIComponent(periodStart)}` : myGroupsPath;
  return fetchOrganisationJson<MyGroupsResponse>(path, "my-groups", signal);
}

/**
 * Calls `GET /api/reporting/me/groups/daily-spend` and interprets the result.
 *
 * @param periodStart - The first day of the reporting period (`YYYY-MM-DD`), or omitted for the current period.
 * @returns Each of the current person's own groupings' day-by-day spend series, a permission denial, or an error.
 */
export async function fetchMyGroupsDailySpend(
  periodStart?: string,
  signal?: AbortSignal,
): Promise<OrganisationQueryResult<MyGroupsDailySpendResponse>> {
  const path = periodStart ? `${myGroupsDailySpendPath}?periodStart=${encodeURIComponent(periodStart)}` : myGroupsDailySpendPath;
  return fetchOrganisationJson<MyGroupsDailySpendResponse>(path, "my-groups-daily-spend", signal);
}

/**
 * Calls `GET /api/reporting/me/groups/{groupId}` and interprets the result. The first group in `groups` is the one asked for; the rest are the groups directly beneath it.
 *
 * @param groupId - The division, department or entitlement group.
 * @param periodStart - The first day of the reporting period (`YYYY-MM-DD`), or omitted for the current period.
 * @returns The group and its children's spend, a permission denial, or an error (including not found).
 */
export async function fetchMyGroup(groupId: string, periodStart?: string, signal?: AbortSignal): Promise<OrganisationQueryResult<MyGroupsResponse>> {
  const path = `${myGroupsPath}/${encodeURIComponent(groupId)}${periodStart ? `?periodStart=${encodeURIComponent(periodStart)}` : ""}`;
  return fetchOrganisationJson<MyGroupsResponse>(path, "my-group", signal);
}

/**
 * Calls `GET /api/reporting/me/groups/{groupId}/daily-spend` and interprets the result.
 *
 * @param groupId - The division, department or entitlement group.
 * @param periodStart - The first day of the reporting period (`YYYY-MM-DD`), or omitted for the current period.
 * @returns The group's and its children's day-by-day spend series, a permission denial, or an error.
 */
export async function fetchMyGroupDailySpend(
  groupId: string,
  periodStart?: string,
  signal?: AbortSignal,
): Promise<OrganisationQueryResult<MyGroupsDailySpendResponse>> {
  const path = `${myGroupsPath}/${encodeURIComponent(groupId)}/daily-spend${periodStart ? `?periodStart=${encodeURIComponent(periodStart)}` : ""}`;
  return fetchOrganisationJson<MyGroupsDailySpendResponse>(path, "my-group-daily-spend", signal);
}

/**
 * Calls `GET /api/reporting/me/groups/{groupId}/usage-by-person` and interprets the result. Names individuals.
 *
 * @param groupId - The division, department or entitlement group.
 * @param periodStart - The first day of the reporting period (`YYYY-MM-DD`), or omitted for the current period.
 * @returns Each member's spend, largest first, a permission denial, or an error.
 */
export async function fetchMyGroupPersonUsage(
  groupId: string,
  periodStart?: string,
  signal?: AbortSignal,
): Promise<OrganisationQueryResult<OrganisationPersonUsageResponse>> {
  const path = `${myGroupsPath}/${encodeURIComponent(groupId)}/usage-by-person${periodStart ? `?periodStart=${encodeURIComponent(periodStart)}` : ""}`;
  return fetchOrganisationJson<OrganisationPersonUsageResponse>(path, "my-group-person-usage", signal);
}

/**
 * Calls `GET /api/reporting/organisation/spend/daily-spend` and interprets the result.
 *
 * @param periodStart - The first day of the reporting period (`YYYY-MM-DD`), or omitted for the current period.
 * @returns Each Organisation grouping's day-by-day spend series for the requested period, a permission denial, or an error.
 */
export async function fetchOrganisationDailySpend(
  periodStart?: string,
  signal?: AbortSignal,
): Promise<OrganisationQueryResult<OrganisationDailySpendResponse>> {
  const path = periodStart
    ? `${organisationDailySpendPath}?periodStart=${encodeURIComponent(periodStart)}`
    : organisationDailySpendPath;
  return fetchOrganisationJson<OrganisationDailySpendResponse>(path, "organisation-daily-spend", signal);
}

/**
 * Calls `GET /api/reporting/organisation/trend` and interprets the result: the months ending at the month containing
 * `periodStart` (the current month when absent), twelve unless `months` says otherwise (at most 24).
 *
 * @param periodStart - Any day within the latest month, or undefined for the current month.
 * @param months - How many months, or undefined for twelve.
 * @param signal - Aborts the request.
 * @returns The trend, a permission denial, an error, or an abort.
 */
export async function fetchOrganisationTrend(periodStart?: string, months?: number, signal?: AbortSignal): Promise<OrganisationQueryResult<OrganisationTrendResponse>> {
  const query = new URLSearchParams();
  if (periodStart) {
    query.set("periodStart", periodStart);
  }
  if (months !== undefined) {
    query.set("months", String(months));
  }
  const path = query.size > 0 ? `${organisationTrendPath}?${query.toString()}` : organisationTrendPath;
  return fetchOrganisationJson<OrganisationTrendResponse>(path, "organisation-trend", signal);
}

/**
 * Calls `GET /api/reporting/organisation/usage-by-person` and interprets the result. Names individuals, so
 * the endpoint refuses a caller without `consumption.read.others` with `denied`.
 *
 * @param periodStart - The first day of the reporting period (`YYYY-MM-DD`), or omitted for the current period.
 * @returns Each person's spend for the requested period, a permission denial, or an error.
 */
export async function fetchOrganisationPersonUsage(
  periodStart?: string,
  signal?: AbortSignal,
): Promise<OrganisationQueryResult<OrganisationPersonUsageResponse>> {
  const path = periodStart ? `${organisationPersonUsagePath}?periodStart=${encodeURIComponent(periodStart)}` : organisationPersonUsagePath;
  return fetchOrganisationJson<OrganisationPersonUsageResponse>(path, "organisation-person-usage", signal);
}

/**
 * Calls `POST /api/reporting/unused-entitlement/baseline/capture` to record a new `SC-9` snapshot.
 *
 * @returns The capture outcome, a permission denial, or an error.
 */
export async function captureUnusedSeatBaseline(): Promise<OrganisationQueryResult<CaptureBaselineResponse>> {
  try {
    const response = await apiFetch(baselineCapturePath, {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (response.status === 403) {
      return { kind: "denied" };
    }

    if (!response.ok) {
      return { kind: "error", message: `The baseline-capture endpoint returned ${String(response.status)}.` };
    }

    const body = (await response.json()) as CaptureBaselineResponse;
    return { kind: "ok", response: body };
  } catch {
    return { kind: "error", message: "The baseline-capture endpoint could not be reached." };
  }
}

/**
 * Calls `POST /api/reporting/unused-entitlement/decisions` to confirm or dismiss a finding —
 * records a decision about the finding itself, never an entitlement change.
 *
 * @param vendorId - The vendor the finding is about.
 * @param personId - The person the finding is about.
 * @param findingKind - Which finding this decides — `dormancy` or `departed`.
 * @param decision - The decision — `confirmed` or `dismissed`.
 * @param note - An optional free-text note.
 * @returns Whether the decision was recorded, a permission denial, or an error.
 */
export async function recordDormancyDecision(
  vendorId: string,
  personId: string,
  findingKind: "dormancy" | "departed",
  decision: "confirmed" | "dismissed",
  note?: string,
): Promise<OrganisationQueryResult<null>> {
  try {
    const response = await apiFetch(decisionsPath, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ vendorId, personId, findingKind, decision, note: note ?? null }),
      cache: "no-store",
    });

    if (response.status === 403) {
      return { kind: "denied" };
    }

    if (!response.ok) {
      return { kind: "error", message: `The decisions endpoint returned ${String(response.status)}.` };
    }

    return { kind: "ok", response: null };
  } catch {
    return { kind: "error", message: "The decisions endpoint could not be reached." };
  }
}

/** Which export this phase's screens offer, and the path/filename pair each one downloads. */
export type ExportKind = "unused-entitlement" | "unattributed" | "organisation-spend" | "unused-seat-baseline";

const exportPaths: Record<ExportKind, { path: string; filename: string }> = {
  "unused-entitlement": { path: unusedEntitlementExportPath, filename: "unused-entitlement.csv" },
  unattributed: { path: unattributedExportPath, filename: "unattributed-consumption.csv" },
  "organisation-spend": { path: organisationSpendExportPath, filename: "organisation-spend.csv" },
  "unused-seat-baseline": { path: baselineExportPath, filename: "unused-seat-baseline.csv" },
};

/**
 * Downloads one of this phase's CSV exports. The export endpoint enforces the same permission as
 * its screen — a caller without it gets `denied` here exactly as the screen itself would, not just
 * a hidden button — and every export carries the same age/completeness annotations the screen shows
 * (the brief's own "the export is what ends up in somebody's slide").
 *
 * @param kind - Which export to download.
 * @returns Whether the download started, a permission denial, or an error.
 */
export async function downloadCsvExport(kind: ExportKind): Promise<OrganisationQueryResult<null>> {
  const { path, filename } = exportPaths[kind];

  try {
    const response = await apiFetch(path, { method: "GET", credentials: "same-origin", headers: { Accept: "text/csv" }, cache: "no-store" });

    if (response.status === 403) {
      return { kind: "denied" };
    }

    if (!response.ok) {
      return { kind: "error", message: `The export endpoint returned ${String(response.status)}.` };
    }

    const csvText = await response.text();
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      URL.revokeObjectURL(url);
    }

    return { kind: "ok", response: null };
  } catch {
    return { kind: "error", message: "The export endpoint could not be reached." };
  }
}

async function fetchOrganisationJson<TResponse>(
  path: string,
  label: string,
  signal?: AbortSignal,
): Promise<OrganisationQueryResult<TResponse>> {
  try {
    const response = await apiFetch(path, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal,
    });

    if (response.status === 403) {
      return { kind: "denied" };
    }

    if (!response.ok) {
      return { kind: "error", message: `The ${label} endpoint returned ${String(response.status)}.` };
    }

    const body = (await response.json()) as TResponse;
    return { kind: "ok", response: body };
  } catch (error) {
    // A caller-initiated abort (StrictMode's double-invoked effect superseding itself, or a
    // component unmounting mid-request) is not a failure - it just means the caller no longer
    // wants this response, so surface it distinctly rather than as a user-facing error.
    if (error instanceof DOMException && error.name === "AbortError") {
      return { kind: "aborted" };
    }

    return { kind: "error", message: `The ${label} endpoint could not be reached.` };
  }
}

/** One Division, Department or group in the budget report for a month (`BudgetReportUnitResponse`). */
export interface BudgetReportUnitResponse {
  kind: "Division" | "Department" | "Group";
  id: string;
  name: string;
  parentKind: "Division" | "Department" | "Group" | null;
  parentId: string | null;
  /** The budget for the month in whole USD, explicit or implied; null when the unit has no budget at all (not $0). */
  budgetUsd: number | null;
  /** Whether `budgetUsd` is implied from the units beneath, rather than set on this unit. */
  budgetIsImplied: boolean;
  /** Whether the hard stop applies to the unit, from itself or an ancestor. */
  effectiveHardStop: boolean;
  /** The unit's spend for the month, in USD: Capex and Opex together. */
  spendUsd: number;
  /** The budget less the spend that counts against it; negative is an overspend. Null when the unit has no budget. */
  differenceUsd: number | null;
  /** Whether the spend that counts against the budget exceeds it. A unit with no budget is never over budget. */
  overBudget: boolean;
  /** Whether the unit is Capex, because it or an ancestor is marked as Capex. */
  effectiveCapex: boolean;
  /** The part of `spendUsd` made by people attributed to a Capex unit within this one. */
  capexSpendUsd: number;
  /** The spend set against the budget: all of `spendUsd` when Capex counts against a budget, otherwise the Opex part only. */
  countedSpendUsd: number;
}

/** The body of `GET /api/reporting/organisation/budgets` (`BudgetReportResponse`): every unit, over-budget first. */
export interface BudgetReportResponse {
  generatedAt: string;
  asAt: string;
  periodStart: string;
  periodEnd: string;
  currency: string;
  /** The spend of people in no group at the month's reference instant. It is in no unit's figures. */
  notInAGroupSpendUsd: number;
  units: BudgetReportUnitResponse[];
  /** The system setting in force: whether spend by a Capex unit counts against its budget. Capex spend is reported either way. */
  capexCountsAgainstBudget: boolean;
}

/**
 * Calls `GET /api/reporting/organisation/budgets` for one calendar month and interprets the result.
 *
 * @param periodStart - The first day of the month (`YYYY-MM-DD`).
 * @param signal - Aborts the request.
 * @returns The budget report, a permission denial, an error or an abort.
 */
export async function fetchOrganisationBudgets(periodStart: string, signal?: AbortSignal): Promise<OrganisationQueryResult<BudgetReportResponse>> {
  return fetchOrganisationJson<BudgetReportResponse>(`/api/reporting/organisation/budgets?periodStart=${encodeURIComponent(periodStart)}`, "organisation-budgets", signal);
}

/** One month of `GET /api/reporting/organisation/budgets/monthly` (`OrganisationMonthlyBudgetResponse`). */
export interface OrganisationMonthlyBudgetResponse {
  periodStart: string;
  /** The sum of the top-level units' budgets, explicit or implied, in whole USD; null when no unit had one. */
  budgetUsd: number | null;
}

/** The body of `GET /api/reporting/organisation/budgets/monthly` (`OrganisationMonthlyBudgetsResponse`): earliest month first. */
export interface OrganisationMonthlyBudgetsResponse {
  currency: string;
  months: OrganisationMonthlyBudgetResponse[];
}

/**
 * Calls `GET /api/reporting/organisation/budgets/monthly` for the months from `from` to `to`, both included, and interprets the result.
 *
 * @param from - The first day of the first month (`YYYY-MM-DD`).
 * @param to - The first day of the last month (`YYYY-MM-DD`).
 * @param signal - Aborts the request.
 * @returns The organisation's budget for each month, a permission denial, an error or an abort.
 */
export async function fetchOrganisationMonthlyBudgets(from: string, to: string, signal?: AbortSignal): Promise<OrganisationQueryResult<OrganisationMonthlyBudgetsResponse>> {
  return fetchOrganisationJson<OrganisationMonthlyBudgetsResponse>(
    `/api/reporting/organisation/budgets/monthly?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    "organisation-monthly-budgets",
    signal,
  );
}
